/**
 * Selecting notes, and moving or deleting them together.
 *
 * Kept apart from the page because these are the rules that decide what a
 * destructive action applies to, and "what exactly is about to be deleted"
 * deserves to be stated somewhere a test can read it.
 */

/**
 * The drag payload's MIME type.
 *
 * A custom type rather than `text/plain`: a note dragged onto a text field
 * would otherwise paste its uuid, and any text dragged in from outside the app
 * would look to a folder like a note being dropped on it.
 */
export const NOTE_DRAG_MIME = 'application/x-tap-note-ids';

/**
 * A folder drag, under its own type.
 *
 * Distinct from the note type so one drop handler can tell them apart. Sharing
 * one would mean a folder dropped on a folder being re-filed as though it were
 * a note — which is a uuid written into user_notes.folder_id that matches no
 * note at all.
 */
export const FOLDER_DRAG_MIME = 'application/x-tap-folder-id';

/** Ids as they travel on a drag. */
export function dragPayload(ids: string[]): string {
  return ids.join(',');
}

/**
 * Ids out of a drop, with anything malformed dropped.
 *
 * Never throws. A drop carrying junk is something the browser handed us, not
 * something to break the page over.
 */
export function parseDragPayload(raw: string): string[] {
  return raw
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '');
}

/**
 * What a drag actually moves.
 *
 * Dragging a card that is part of a selection moves the whole selection —
 * that is what "drag the selected cluster" means, and picking up one card out
 * of five ticked ones would look like the other four were ignored. Dragging a
 * card that is NOT selected moves just that card and leaves the selection
 * alone, because otherwise a stray drag silently refiles everything ticked.
 */
export function dragSet(noteId: string, selected: ReadonlySet<string>): string[] {
  return selected.has(noteId) ? [...selected] : [noteId];
}

/** Add or remove one id. Returns a new set; never mutates the one given. */
export function toggleSelected(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * The selection, narrowed to what is actually on screen.
 *
 * A note can be selected and then filtered out by a search or a folder change.
 * Acting on the raw set would delete things the person can no longer see, so
 * every destructive action goes through this first.
 */
export function selectionWithin(
  selected: ReadonlySet<string>,
  visible: { id: string }[],
): string[] {
  const onScreen = new Set(visible.map((note) => note.id));
  return [...selected].filter((id) => onScreen.has(id));
}

/** Whether every visible note is ticked, for the select-all control. */
export function allSelected(selected: ReadonlySet<string>, visible: { id: string }[]): boolean {
  return visible.length > 0 && visible.every((note) => selected.has(note.id));
}

export function selectAll(visible: { id: string }[]): Set<string> {
  return new Set(visible.map((note) => note.id));
}

/** The label on the batch bar. */
export function selectionLabel(count: number): string {
  return `Selected (${String(count)})`;
}
